import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Search, Plus, ChevronDown, ChevronRight,
  Building2, Hammer, CheckCircle, PauseCircle,
  FileEdit, Archive, TrendingUp
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ChantierCard } from './ChantierCard';

interface ChantierListItem {
  id: string;
  nom_chantier: string;
  client: string;
  statut: string;
  etat_chantier: string;
  rentabilite: number;
  categorie?: string;
  date_debut_prevue?: string;
  date_fin_estimee?: string;
  pictogramme?: string;
  couleur?: string;
}

// Groupes par etat_chantier pour l'affichage
const GROUPES_ETATS = [
  {
    id: 'en_cours',
    label: 'En cours',
    etats: ['en_cours'],
    icon: Hammer,
    defaultExpanded: true,
  },
  {
    id: 'planifie',
    label: 'Planifié',
    etats: ['planifie'],
    icon: Building2,
    defaultExpanded: false,
  },
  {
    id: 'suspendu',
    label: 'Suspendu',
    etats: ['suspendu'],
    icon: PauseCircle,
    defaultExpanded: false,
  },
  {
    id: 'termine',
    label: 'Terminé',
    etats: ['termine'],
    icon: CheckCircle,
    defaultExpanded: false,
  },
  {
    id: 'projections',
    label: 'Projections',
    etats: ['brouillon', 'devis_envoye'],
    icon: FileEdit,
    defaultExpanded: false,
  },
  {
    id: 'archive',
    label: 'Archivé',
    etats: ['annule'],
    isArchive: true, // Filtre spécial sur statut='archive'
    icon: Archive,
    defaultExpanded: false,
  },
];

interface ChantiersSidebarProps {
  selectedChantierId: string;
  onSelectChantier: (id: string) => void;
  chantiers?: ChantierListItem[]; // Chantiers passés par le parent
  loading?: boolean; // Loading state du parent
  rentabiliteLabel?: string; // Libellé pour la rentabilité (par défaut: "Marge actuelle")
}

export function ChantiersSidebar({
  selectedChantierId,
  onSelectChantier,
  chantiers: chantiersFromParent,
  loading: loadingFromParent,
  rentabiliteLabel = "Marge actuelle"
}: ChantiersSidebarProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  // Accordéon exclusif : un seul groupe ouvert à la fois
  // Par défaut, ouvrir le premier groupe qui a defaultExpanded=true
  const [openGroupId, setOpenGroupId] = useState<string | null>(
    GROUPES_ETATS.find(g => g.defaultExpanded)?.id ?? null
  );
  const [localChantiers, setLocalChantiers] = useState<ChantierListItem[]>([]);
  const [localLoading, setLocalLoading] = useState(!chantiersFromParent);
  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);

  // Utiliser les chantiers du parent si fournis, sinon les locaux
  const chantiers = chantiersFromParent || localChantiers;
  const loading = chantiersFromParent ? (loadingFromParent || false) : localLoading;

  // Charger les chantiers localement (seulement si pas fournis par le parent)
  // silentRefresh = true → ne pas afficher de loader (pour refresh en arrière-plan)
  const loadChantiers = useCallback(async (silentRefresh = false) => {
    // Si les chantiers sont fournis par le parent, ne pas charger localement
    if (chantiersFromParent) return;
    if (!user) return;

    try {
      // Ne pas afficher le loader si c'est un refresh silencieux
      if (!silentRefresh) {
        setLocalLoading(true);
      }

      // Récupérer l'entreprise
      const { data: entreprise } = await supabase
        .from('entreprises')
        .select('id')
        .eq('proprietaire_user_id', user.id)
        .maybeSingle();

      if (!entreprise) {
        setLocalChantiers([]);
        setEntrepriseId(null);
        return;
      }

      setEntrepriseId(entreprise.id);

      // Récupérer les chantiers
      const { data: chantiersData } = await supabase
        .from('chantiers')
        .select('*')
        .eq('entreprise_id', entreprise.id)
        .order('date_creation', { ascending: false });

      if (!chantiersData) {
        setLocalChantiers([]);
        return;
      }

      // Récupérer les métriques pour chaque chantier
      const chantiersAvecMetriques = await Promise.all(
        chantiersData.map(async (c) => {
          const { data: metricsData } = await supabase
            .from('chantier_metrics_realtime')
            .select('metrics')
            .eq('chantier_id', c.id)
            .maybeSingle();

          const metrics = metricsData?.metrics as any;
          const rentabilite = metrics?.marge_finale_pct || 0;

          return {
            ...c,
            etat_chantier: c.etat_chantier || 'en_cours',
            rentabilite,
          };
        })
      );

      setLocalChantiers(chantiersAvecMetriques);
    } catch (error) {
      console.error('Erreur chargement chantiers sidebar:', error);
    } finally {
      // Ne pas mettre loading = false si c'était un refresh silencieux
      if (!silentRefresh) {
        setLocalLoading(false);
      }
    }
  }, [user, chantiersFromParent]);

  useEffect(() => {
    // Ne charger que si pas de chantiers fournis par le parent
    if (!chantiersFromParent) {
      loadChantiers();
    }
  }, [loadChantiers, chantiersFromParent]);

  // Subscription Realtime (seulement si pas de chantiers fournis)
  useEffect(() => {
    if (chantiersFromParent) return; // Le parent gère le realtime
    if (!user || !entrepriseId) return;

    const channel = supabase
      .channel(`sidebar_chantiers:${entrepriseId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chantiers', filter: `entreprise_id=eq.${entrepriseId}` },
        () => loadChantiers(true) // Silent refresh pour ne pas afficher de loader
      )
      .on(
        'postgres_changes',
        // chantier_metrics_realtime n'a pas de colonne entreprise_id — RLS filtre côté serveur
        { event: '*', schema: 'public', table: 'chantier_metrics_realtime' },
        () => loadChantiers(true) // Silent refresh pour ne pas afficher de loader
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, entrepriseId, loadChantiers]);

  // Écouter les événements custom pour les mises à jour (depuis AffectationsList, useChantierStatus)
  useEffect(() => {
    // Seulement si on gère les chantiers localement (pas fournis par parent)
    if (chantiersFromParent) return;

    const handleMetricsUpdate = () => {
      if (import.meta.env.DEV) console.log('[ChantiersSidebar] Événement chantier-metrics-updated reçu, rechargement silencieux...');
      loadChantiers(true); // Silent refresh pour ne pas afficher de loader
    };

    const handleStatusUpdate = () => {
      if (import.meta.env.DEV) console.log('[ChantiersSidebar] Événement chantier-status-updated reçu, rechargement silencieux...');
      loadChantiers(true); // Silent refresh pour ne pas afficher de loader
    };

    window.addEventListener('chantier-metrics-updated', handleMetricsUpdate);
    window.addEventListener('chantier-status-updated', handleStatusUpdate);
    return () => {
      window.removeEventListener('chantier-metrics-updated', handleMetricsUpdate);
      window.removeEventListener('chantier-status-updated', handleStatusUpdate);
    };
  }, [chantiersFromParent, loadChantiers]);

  // Filtrer par recherche
  const chantiersFiltres = useMemo(() => {
    if (!searchQuery.trim()) return chantiers;

    const query = searchQuery.toLowerCase();
    return chantiers.filter(c =>
      c.nom_chantier.toLowerCase().includes(query) ||
      c.client.toLowerCase().includes(query)
    );
  }, [chantiers, searchQuery]);

  // Nombre total de chantiers non-archivés (seuil pour afficher la recherche)
  const totalChantiersNonArchives = useMemo(() =>
    chantiers.filter(c => c.etat_chantier !== 'archive').length,
    [chantiers]
  );

  // Reset la recherche si le nombre de chantiers passe sous le seuil
  useEffect(() => {
    if (totalChantiersNonArchives <= 9) {
      setSearchQuery('');
    }
  }, [totalChantiersNonArchives]);

  // Grouper par etat_chantier (et statut pour les archives)
  const chantiersParGroupe = useMemo(() => {
    const grouped: Record<string, ChantierListItem[]> = {};

    GROUPES_ETATS.forEach(group => {
      if ((group as any).isArchive) {
        // Groupe "Archive" : filtrer sur etat_chantier='archive'
        grouped[group.id] = chantiersFiltres.filter(c => c.etat_chantier === 'archive');
      } else {
        // Autres groupes : filtrer sur etat_chantier ET pas archivé
        // Si etat_chantier est null/undefined, on considère que c'est 'en_cours' par défaut
        grouped[group.id] = chantiersFiltres.filter(c => {
          const etat = c.etat_chantier || 'en_cours';
          return group.etats.includes(etat) && c.etat_chantier !== 'archive';
        });
      }
    });

    return grouped;
  }, [chantiersFiltres]);

  // Toggle expand/collapse d'un groupe (accordéon : un seul ouvert à la fois)
  const toggleGroup = (groupId: string) => {
    setOpenGroupId(prev => prev === groupId ? null : groupId);
  };

  // Créer un nouveau chantier
  const handleNewChantier = () => {
    navigate('/projects');
  };

  return (
    <aside className="w-[200px] h-full bg-card border-r border-border flex flex-col">

      {/* Header */}
      <div className="p-4 border-b border-border">
        {/* Titre + Bouton nouveau */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">
            Chantiers
          </h2>
          <button
            onClick={handleNewChantier}
            className="p-1.5 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors"
            title="Nouveau chantier"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Barre de recherche — masquée si ≤ 9 chantiers non-archivés */}
        {totalChantiersNonArchives > 9 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              className="w-full pl-9 pr-4 py-2 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            />
          </div>
        )}
      </div>

      {/* Liste des chantiers */}
      <div className="flex-1 overflow-y-auto p-2 chantiers-sidebar">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-2">
            {GROUPES_ETATS.map(group => {
              const chantiersGroupe = chantiersParGroupe[group.id] || [];
              const count = chantiersGroupe.length;
              const isExpanded = openGroupId === group.id;
              const Icon = group.icon;

              // Ne pas afficher le groupe s'il est vide
              if (count === 0) return null;

              return (
                <div key={group.id}>
                  {/* Header du groupe */}
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span>{group.label}</span>
                      <span className="text-xs text-foreground/60">({count})</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>

                  {/* Liste des chantiers du groupe */}
                  {isExpanded && (
                    <div className="mt-1 space-y-1">
                      {chantiersGroupe.map(chantier => (
                        <ChantierCard
                          key={chantier.id}
                          chantier={chantier}
                          isSelected={chantier.id === selectedChantierId}
                          onClick={() => onSelectChantier(chantier.id)}
                          rentabiliteLabel={rentabiliteLabel}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Message si aucun résultat de recherche */}
        {!loading && searchQuery && chantiersFiltres.length === 0 && (
          <div className="text-center py-8">
            <Search className="w-8 h-8 mx-auto mb-2 text-foreground/40" />
            <p className="text-sm text-foreground/60">
              Aucun chantier trouvé pour "{searchQuery}"
            </p>
          </div>
        )}

        {/* Message si aucun chantier du tout */}
        {!loading && !searchQuery && chantiers.length === 0 && (
          <div className="text-center py-8">
            <Building2 className="w-8 h-8 mx-auto mb-2 text-foreground/40" />
            <p className="text-sm text-foreground/60">
              Aucun chantier disponible
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-border">
        <button
          onClick={() => navigate('/rentabilite')}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground/70 hover:text-foreground hover:bg-muted rounded-lg transition-colors"
        >
          <TrendingUp className="w-4 h-4" />
          <span>Vue globale rentabilité</span>
        </button>
      </div>
    </aside>
  );
}
